import React, { useEffect, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import styles from './AvatarPickerModal.module.css';
import {
  GEO, MONO, CHARACTERS,
  svgCharacter, renderAvatarSvg, svgToPngDataUrl,
  type AvatarConfig,
} from './avatarArt';

type Mode = 'geometric' | 'cartoon' | 'monogram';

interface AvatarPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the rasterized PNG data URL + the config that produced it. */
  onUseGenerated: (pngDataUrl: string, config: AvatarConfig) => void | Promise<void>;
  /** Trigger the existing profile-photo file input + crop flow. */
  onUploadPhoto: () => void;
  displayName?: string;
  handle?: string;
  /** Slice 2: prefill from the user's last saved avatar config. */
  initialConfig?: AvatarConfig | null;
}

const UPLOAD_PREVIEW =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">' +
  '<circle cx="50" cy="50" r="50" fill="#cbd5e1"/>' +
  '<g stroke="#64748b" stroke-width="4" fill="none" stroke-linecap="round">' +
  '<circle cx="50" cy="42" r="13"/><path d="M28 78 Q50 60 72 78"/></g></svg>';

export default function AvatarPickerModal({
  open, onClose, onUseGenerated, onUploadPhoto, displayName, handle, initialConfig,
}: AvatarPickerModalProps) {
  const { t } = useAppTranslation('userProfile');

  const initInitials =
    (displayName || '').trim().split(/\s+/).map((w) => w[0] || '').join('').slice(0, 2).toUpperCase() || 'A';

  const [tab, setTab] = useState<'avatar' | 'upload'>('avatar');
  const [mode, setMode] = useState<Mode>(
    initialConfig?.kind === 'geometric' ? 'geometric'
      : initialConfig?.kind === 'monogram' ? 'monogram' : 'cartoon',
  );
  const [geo, setGeo] = useState(
    initialConfig?.kind === 'geometric'
      ? { pattern: initialConfig.pattern, palette: initialConfig.palette, bg: initialConfig.bg }
      : { pattern: 0, palette: 0, bg: 0 },
  );
  const [mono, setMono] = useState(
    initialConfig?.kind === 'monogram'
      ? { color: initialConfig.color, shape: initialConfig.shape, initials: initialConfig.initials }
      : { color: 0, shape: 0, initials: initInitials },
  );
  const [charKey, setCharKey] = useState<string>(
    initialConfig?.kind === 'cartoon' ? initialConfig.character : CHARACTERS[0].key,
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const config: AvatarConfig =
    mode === 'geometric' ? { kind: 'geometric', ...geo }
      : mode === 'monogram' ? { kind: 'monogram', ...mono }
        : { kind: 'cartoon', character: charKey };

  const previewSvg = tab === 'upload' ? UPLOAD_PREVIEW : renderAvatarSvg(config, 100);

  const handleUse = async () => {
    setBusy(true);
    try {
      const png = await svgToPngDataUrl(renderAvatarSvg(config, 512), 512);
      await onUseGenerated(png, config);
      onClose();
    } catch {
      setBusy(false);
    }
  };

  const rand = (n: number) => Math.floor(Math.random() * n);
  const surprise = () => {
    if (mode === 'geometric') setGeo({ pattern: rand(GEO.pattern.length), palette: rand(GEO.palette.length), bg: rand(GEO.bg.length) });
    else setMono((m) => ({ ...m, color: rand(MONO.color.length), shape: rand(MONO.shape.length) }));
  };

  const seg = (m: Mode, label: string) => (
    <button className={mode === m ? styles.segOn : ''} onClick={() => setMode(m)} type="button">{label}</button>
  );

  return (
    <div className={styles.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label={t('avatarPicker.title', 'Choose your avatar')}>
        <div className={styles.head}>
          <h2>{t('avatarPicker.title', 'Choose your avatar')}</h2>
          <button className={styles.close} onClick={onClose} type="button" aria-label={t('avatarPicker.close', 'Close')}>✕</button>
        </div>

        <div className={styles.tabs}>
          <button type="button" className={`${styles.tab} ${tab === 'avatar' ? styles.tabOn : ''}`} onClick={() => setTab('avatar')}>🎨 {t('avatarPicker.tabCartoon', 'Cartoon Avatar')}</button>
          <button type="button" className={`${styles.tab} ${tab === 'upload' ? styles.tabOn : ''}`} onClick={() => setTab('upload')}>📷 {t('avatarPicker.tabUpload', 'Upload a Photo')}</button>
        </div>

        <div className={styles.scroll}>
          <div className={styles.body}>
            <div className={styles.previewCol}>
              <div className={styles.bigAvatar} dangerouslySetInnerHTML={{ __html: previewSvg }} />
              <div className={styles.ctxCard}>
                <div className={styles.ctxCover} />
                <div className={styles.ctxAv} dangerouslySetInnerHTML={{ __html: previewSvg }} />
                <div className={styles.ctxName}>{displayName || 'Your name'}</div>
                {handle ? <div className={styles.ctxHandle}>{handle}</div> : null}
              </div>
            </div>

            {tab === 'avatar' ? (
              <div className={styles.controls}>
                <div className={styles.seg}>
                  {seg('geometric', t('avatarPicker.modeGeometric', 'Geometric'))}
                  {seg('cartoon', t('avatarPicker.modeCartoon', 'Cartoon'))}
                  {seg('monogram', t('avatarPicker.modeMonogram', 'Monogram'))}
                </div>

                {mode === 'cartoon' && (
                  <div className={styles.grp}>
                    <h4>{t('avatarPicker.pickCharacter', 'Pick a character')}</h4>
                    <div className={styles.gallery}>
                      {CHARACTERS.map((c) => (
                        <button
                          key={c.key} type="button" title={c.name}
                          className={`${styles.face} ${charKey === c.key ? styles.faceOn : ''}`}
                          onClick={() => setCharKey(c.key)}
                          dangerouslySetInnerHTML={{ __html: svgCharacter(c.key, 100) }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {mode === 'geometric' && (
                  <>
                    <div className={styles.grp}><h4>{t('avatarPicker.pattern', 'Pattern')}</h4>
                      <div className={styles.chips}>{GEO.pattern.map((p, i) => (
                        <button key={p} type="button" className={`${styles.chip} ${geo.pattern === i ? styles.chipOn : ''}`} onClick={() => setGeo({ ...geo, pattern: i })}>{t(`avatarPicker.patternOpt.${p}`, p)}</button>
                      ))}</div>
                    </div>
                    <div className={styles.grp}><h4>{t('avatarPicker.palette', 'Palette')}</h4>
                      <div className={styles.chips}>{GEO.palette.map((p, i) => (
                        <button key={i} type="button" aria-label={`palette ${i + 1}`} className={`${styles.sw} ${geo.palette === i ? styles.swOn : ''}`} style={{ backgroundImage: `linear-gradient(135deg, ${p[0]}, ${p[1]})` }} onClick={() => setGeo({ ...geo, palette: i })} />
                      ))}</div>
                    </div>
                    <div className={styles.grp}><h4>{t('avatarPicker.background', 'Background')}</h4>
                      <div className={styles.chips}>{GEO.bg.map((c, i) => (
                        <button key={c} type="button" aria-label={`background ${i + 1}`} className={`${styles.sw} ${geo.bg === i ? styles.swOn : ''}`} style={{ background: c }} onClick={() => setGeo({ ...geo, bg: i })} />
                      ))}</div>
                    </div>
                  </>
                )}

                {mode === 'monogram' && (
                  <>
                    <div className={styles.grp}><h4>{t('avatarPicker.initials', 'Initials')}</h4>
                      <input className={styles.initInput} maxLength={2} value={mono.initials} onChange={(e) => setMono({ ...mono, initials: e.target.value || '?' })} />
                    </div>
                    <div className={styles.grp}><h4>{t('avatarPicker.color', 'Color')}</h4>
                      <div className={styles.chips}>{MONO.color.map((p, i) => (
                        <button key={i} type="button" aria-label={`color ${i + 1}`} className={`${styles.sw} ${mono.color === i ? styles.swOn : ''}`} style={{ backgroundImage: `linear-gradient(135deg, ${p[0]}, ${p[1]})` }} onClick={() => setMono({ ...mono, color: i })} />
                      ))}</div>
                    </div>
                    <div className={styles.grp}><h4>{t('avatarPicker.shape', 'Shape')}</h4>
                      <div className={styles.chips}>{MONO.shape.map((sh, i) => (
                        <button key={sh} type="button" className={`${styles.chip} ${mono.shape === i ? styles.chipOn : ''}`} onClick={() => setMono({ ...mono, shape: i })}>{t(`avatarPicker.shapeOpt.${sh}`, sh)}</button>
                      ))}</div>
                    </div>
                  </>
                )}

                {mode !== 'cartoon' && (
                  <button className={styles.surprise} type="button" onClick={surprise}>✨ {t('avatarPicker.surprise', 'Surprise me')}</button>
                )}
              </div>
            ) : (
              <div className={styles.controls}>
                <div className={styles.upload}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                  <div>{t('avatarPicker.uploadPrompt', 'Drag a photo here, or browse your device')}</div>
                  <button className={styles.upBtn} type="button" onClick={() => { onUploadPhoto(); onClose(); }}>{t('avatarPicker.choosePhoto', 'Choose a photo')}</button>
                  <div style={{ fontSize: 11.5 }}>{t('avatarPicker.uploadHint', 'Crop to a circle after choosing. JPG or PNG, up to 10 MB.')}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.foot}>
          <button className={styles.ghost} type="button" onClick={onClose}>{t('avatarPicker.cancel', 'Cancel')}</button>
          {tab === 'avatar'
            ? <button className={styles.primary} type="button" onClick={handleUse} disabled={busy}>{busy ? t('avatarPicker.saving', 'Saving…') : t('avatarPicker.use', 'Use this avatar')}</button>
            : <span />}
        </div>
      </div>
    </div>
  );
}
